import React, { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import {
  CmpMemberContactAccountActionEnum as Action, CmpMemberLifecycleStatusEnum,
  useGetCmpMemberContactAccountsQuery, useManageCmpMemberContactAccountMutation,
  useGetSampleLoginInfoEmailQuery, LoginInfoEmailTypes,
} from "../../../../graphql/types";
import { contactAccessMutationApolloContext, contactLoginEmailInUseMessage, credentialFollowUpNote, getMemberCredentialLifecycleBlock, isContactEmailRetryRefusal, isMemberLoginEmailInUseRefusal } from "./memberCredentialActions";
import { DesignDialog, DesignDialogButton } from "../../../common/dashboard/DesignDialog";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { memberRequestApolloContext } from "../memberWorkflowShared";
import SendCredentialsDialog, { resolveMemberCredentialEmailPreview } from "./SendCredentialsDialog";
import { downloadTextFile } from "../../Invoicing/invoiceFileDownloads";
import MemberAdministratorCredentials, { MemberAdministratorCredentialsProps } from "./MemberAdministratorCredentials";
import MemberContactAccessGrant from "../MemberContactAccessGrant";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../../utils/cmpOrganizationNoun";
import { contactAccessMissingDetailsMessage } from "../memberContactValidation";
import {
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";

type LegacyMemberCredentialActions = Pick<MemberAdministratorCredentialsProps,
  "onSend" | "onReset" | "disabled" | "hasUnsavedLoginEmail">;

type ContactAccountSuccess = { message: string; highlights?: string[]; note?: string; warning?: string };

// Update Member Form copy. One label for every contact; the backend still
// limits Accounts Payable to invoices and other contacts to everything else.
// The design shows no role note under the box (Monday 13252201937).
export const contactAccessGrantLabel = "Check this box to grant this contact access to your member dashboard. Based on their roles, they will be allowed to view and update various sections such as company profile on the Chamber website and in the Chamber Perks App™ Global Directory, create and manage company perks, view and pay invoices, register for events and update attendee details. Then click on the Send Credentials button.";
const CONTACT_ACCESS_NOT_LOADED_TEXT = "We could not load Member Dashboard access for this contact. Refresh the page to try again.";
export const contactSameAsPrimaryAccessMessage = "This contact is the same as the Primary Contact, so no separate dashboard access is needed.";
export const contactAccessLockedTitle = "Check the Member Dashboard access box above to enable credentials for this contact.";

// Claude Design (8) Update Member: fixed non-primary contacts link the access
// checkbox and the credentials toggle as one draft that Save Changes applies.
// Primary, legacy member-login and additional (custom) contacts keep their
// immediate controls.
export const isContactAccessDraftLinked = (contactId: string) =>
  contactId !== "primaryVoting" && !contactId.startsWith("legacyMemberAccount") && !contactId.startsWith("custom:");

// Save order for access drafts. Turning access off runs before the contact
// save, because the backend refuses an email change while that contact's
// login is active (cap-backend cmpMemberContactLoginGuard, Ajax Sept 2026).
// Turning access on runs after the save, so the login uses the saved email.
export const splitContactAccessDraftIds = (drafts: Record<string, boolean>, contactIds = Object.keys(drafts)) => ({
  beforeSave: contactIds.filter(contactId => drafts[contactId] === false),
  afterSave: contactIds.filter(contactId => drafts[contactId] === true),
});

const getContactAccountSuccess = ({ action, name, email, format, deliveryConfirmed }: {
  action: Action; name: string; email: string; format: "csv" | "html"; deliveryConfirmed: boolean;
}): ContactAccountSuccess | null => {
  const contactName = name.trim() || email;
  if (action === Action.DownloadCredentials) return format === "html"
    ? { message: `The Member Dashboard welcome email for ${contactName} has been downloaded and is ready to print.`, highlights: [contactName], note: credentialFollowUpNote }
    : { message: `The login credentials for ${contactName} have been downloaded successfully.`, highlights: [contactName], note: credentialFollowUpNote };
  if (!deliveryConfirmed) return null;
  if (action === Action.ResetPassword) return {
    message: `The member dashboard password has been reset for ${contactName} only — no other contact’s login is affected. New login details have been emailed to ${email}. This contact will show as not logged in until they sign in with their new password.`,
    highlights: [contactName, "only"],
  };
  if (action === Action.SendCredentials) return {
    message: `The Member Dashboard welcome email has been sent successfully to ${contactName} (${email})!`,
    highlights: [contactName], note: credentialFollowUpNote,
  };
  return null;
};

export default function useMemberContactAccounts({ chamberId, memberId, revision, lifecycleStatus, timeZone, active, readOnly, onCompleted, legacyCredentials,
  accessDrafts, onAccessDraftChange }: {
  chamberId: string; memberId: string; revision: number; lifecycleStatus: CmpMemberLifecycleStatusEnum;
  timeZone?: string | null; active: boolean; readOnly: boolean; onCompleted: (revision: number) => Promise<void>;
  legacyCredentials?: LegacyMemberCredentialActions;
  /** Page-owned access drafts by contact id (true = enable, false = disable). */
  accessDrafts: Record<string, boolean>;
  /** null clears the draft because it matches the saved access again. */
  onAccessDraftChange: (contactId: string, enabled: boolean | null) => void;
}) {
  const noun = useCmpOrganizationNoun();
  const { data, loading, error, refetch } = useGetCmpMemberContactAccountsQuery({
    variables: { input: { chamberId, memberId } }, skip: !active || !chamberId || !memberId,
    context: memberRequestApolloContext, fetchPolicy: "cache-and-network",
  });
  const [mutate, { loading: busy }] = useManageCmpMemberContactAccountMutation({ context: contactAccessMutationApolloContext, fetchPolicy: "no-cache" });
  const [failure, setFailure] = useState<{ contactId: string; message: string } | null>(null);
  const [pending, setPending] = useState<{ contactId: string; name: string; email: string; action: Action } | null>(null);
  // Design (16) Update Member "Reset Contact Credentials?": credentials were
  // sent but the contact never logged in, so a new send replaces them.
  const [resendWarning, setResendWarning] = useState<{ name: string; email: string; sentAtUtcMilli: number; onProceed: () => void } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const credentialLifecycleBlock = getMemberCredentialLifecycleBlock(lifecycleStatus);
  const credentialsAllowed = !credentialLifecycleBlock;
  const sample = useGetSampleLoginInfoEmailQuery({ variables: { chamberMemberId: memberId, memberContactId: pending?.contactId,
    type: pending?.action === Action.ResetPassword ? LoginInfoEmailTypes.ResetPassword : LoginInfoEmailTypes.BrandNew },
    skip: !pending || !previewOpen || !credentialsAllowed, context: memberRequestApolloContext, fetchPolicy: "no-cache" });
  const preview = resolveMemberCredentialEmailPreview({ data: sample.data?.getSampleLoginInfoEmail, loading: sample.loading,
    failed: !!sample.error, blocked: !pending || !previewOpen || !credentialsAllowed });
  const [success, setSuccess] = useState<ContactAccountSuccess | null>(null);
  const inFlight = useRef(false);
  const draftEnabledAt = useRef<Record<string, number>>({});
  const [refreshRequired, setRefreshRequired] = useState(false);
  const scope = `${chamberId}:${memberId}`;
  const currentScope = useRef(scope);
  const accountRevision = useRef({ scope, revision });
  currentScope.current = scope;
  useEffect(() => { setPending(null); setPreviewOpen(false); setFailure(null); setSuccess(null); setRefreshRequired(false); }, [scope]);
  useEffect(() => {
    const previous = accountRevision.current;
    accountRevision.current = { scope, revision };
    if (!active || previous.scope !== scope || previous.revision === revision) return;
    void refetch().catch(() => {
      if (currentScope.current === scope) setRefreshRequired(true);
    });
  }, [active, revision, refetch, scope]);
  const execute = async (contactId: string, action: Action, format: "csv" | "html" = "csv", recipient?: { name: string; email: string },
    expectedRevision = revision): Promise<boolean> => {
    if (inFlight.current || busy || readOnly || refreshRequired) return false;
    if (!credentialsAllowed && (action === Action.SendCredentials || action === Action.ResetPassword || action === Action.DownloadCredentials)) {
      setFailure({ contactId, message: credentialLifecycleBlock });
      return false;
    }
    inFlight.current = true;
    setFailure(null);
    try {
      const result = await mutate({ variables: { input: { chamberId, memberId, contactId, expectedMemberUpdatedAtUtcMilli: expectedRevision, action } } });
      if (currentScope.current !== scope) return false;
      const completed = result.data?.manageCmpMemberContactAccount;
      if (!completed) throw new UserFacingError("We could not confirm the change to Member Dashboard access. Refresh the page before you try again.");
      if (action === Action.DownloadCredentials) {
        const download = completed.credentials;
        if (!download?.password || !download.email || !download.emailHtml) throw new UserFacingError("We could not confirm the password download. Refresh the page before you send or download credentials again.");
        const csv = [["Email", "Temporary Password"], [download.email, download.password]].map(row => row.map(value => `"${value.replace(/"/g, '""')}"`).join(",")).join("\n");
        downloadTextFile({ filename: `Member Contact Credentials.${format}`, content: format === "html" ? download.emailHtml : csv,
          contentType: format === "html" ? "text/html;charset=utf-8" : "text/csv;charset=utf-8" });
      }
      setPending(null);
      setPreviewOpen(false);
      const account = data?.getCmpMemberContactAccounts.find(row => row.contactId === contactId);
      const completedSuccess = getContactAccountSuccess({ action, format, deliveryConfirmed: !!completed.deliveryConfirmed,
        name: recipient?.name ?? account?.name ?? "", email: recipient?.email || account?.email || completed.credentials?.email || "" });
      const accessMessage = action === Action.Disable ? "Contact dashboard access is disabled." : "Contact dashboard access is enabled.";
      const deliveryUnconfirmed = (action === Action.SendCredentials || action === Action.ResetPassword) && !completed.deliveryConfirmed;
      if (deliveryUnconfirmed) {
        setRefreshRequired(true);
        setFailure({ contactId, message: `We could not confirm that the email was sent. Do not send it again. Email ${STAFF_SUPPORT_EMAIL} to check first.` });
      }
      // The design shows no pop-up for an access switch; its ENABLED state is
      // the confirmation. Show the switch result only with a warning.
      const showCompleted = (warning?: string) => {
        if (completedSuccess) setSuccess({ ...completedSuccess, warning });
        else if (warning && !deliveryUnconfirmed) setSuccess({ message: accessMessage, warning });
      };
      showCompleted(completed.trackingWarning || undefined);
      if (completed.trackingWarning) setRefreshRequired(true);
      try {
        accountRevision.current = { scope, revision: completed.memberUpdatedAtUtcMilli };
        await onCompleted(completed.memberUpdatedAtUtcMilli);
        await refetch();
      } catch {
        setRefreshRequired(true);
        showCompleted([completed.trackingWarning, "Your change was saved, but the page did not update. Refresh the page before you make another change."].filter(Boolean).join(" "));
      }
      return true;
    } catch (cause) {
      if (currentScope.current !== scope) return false;
      setPending(null);
      setPreviewOpen(false);
      // Monday 13387351653: an email already in use is refused before any
      // change, so staff can enter another Contact Email and click Save
      // Changes again without a page refresh. Other failures stay locked.
      if (isMemberLoginEmailInUseRefusal(cause)) {
        setFailure({ contactId, message: contactLoginEmailInUseMessage });
        return false;
      }
      // The other email refusals (same chamber, turned-off login, shared
      // contact email) change nothing either and say what to do.
      if (isContactEmailRetryRefusal(cause)) {
        setFailure({ contactId, message: getUserFacingErrorText(cause, contactLoginEmailInUseMessage) });
        return false;
      }
      setRefreshRequired(true);
      setFailure({ contactId, message: getUserFacingErrorText(cause, `We could not change Member Dashboard access for this contact. Refresh the page and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`) });
      return false;
    } finally {
      inFlight.current = false;
    }
  };
  const setAccessDraft = (contactId: string, enabled: boolean) => {
    const account = data?.getCmpMemberContactAccounts.find(row => row.contactId === contactId);
    if (enabled) draftEnabledAt.current[contactId] = Date.now();
    onAccessDraftChange(contactId, enabled === (account?.enabled === true) ? null : enabled);
  };
  /** Applies a saved-contact access draft after the contact section saved. */
  const applyAccessDraft = async (contactId: string, expectedRevision: number): Promise<boolean> => {
    const enabled = accessDrafts[contactId];
    if (enabled === undefined) {
      // The contact saved without an access change: an earlier email refusal
      // for it no longer applies (live Oct 30 2026, S6 retest).
      setFailure((current) => (current?.contactId === contactId && !refreshRequired ? null : current));
      return true;
    }
    const applied = await execute(contactId, enabled ? Action.Enable : Action.Disable, "csv", undefined, expectedRevision);
    if (applied) onAccessDraftChange(contactId, null);
    return applied;
  };
  /** The access box state: the unsaved draft, else the saved account access. */
  const isAccessChecked = (contactId: string) => {
    const account = data?.getCmpMemberContactAccounts.find(row => row.contactId === contactId);
    return accessDrafts[contactId] ?? account?.enabled === true;
  };
  const renderAccessGrant = (contactId: string, sameAsPrimary = false, detailsMissingAfterSave = false) => {
    if (contactId === "primaryVoting") return null;
    const account = data?.getCmpMemberContactAccounts.find(row => row.contactId === contactId);
    const lifecycleAllowsAccess = lifecycleStatus === CmpMemberLifecycleStatusEnum.Active || lifecycleStatus === CmpMemberLifecycleStatusEnum.Pending;
    const savedEnabled = account?.enabled === true;
    const checked = isAccessChecked(contactId);
    // CMP Dashboard (19): the box can be ticked before the name and email are
    // entered; Save Changes checks them. Only a contact that is the Primary
    // Contact cannot get separate access. The other reasons are production
    // safety states from the saved account.
    const nudge = !sameAsPrimary && detailsMissingAfterSave && checked ? contactAccessMissingDetailsMessage : undefined;
    const reason = !account || account.enabled === null || error ? CONTACT_ACCESS_NOT_LOADED_TEXT
      : account.usesLegacyMemberAccount ? "This contact uses the member login. Use Send Credentials in the Member Administrator Contact section."
      : account.credentialActionsBlockedReason || undefined;
    // A blocked reason stops new access, but a saved enabled contact can
    // still be unticked so staff can always remove access.
    const canRemoveOnly = checked && savedEnabled && !!account && !account.usesLegacyMemberAccount && !error && !sameAsPrimary;
    const disabled = busy || loading || readOnly || refreshRequired || !lifecycleAllowsAccess ||
      (!!reason && !canRemoveOnly) || (sameAsPrimary && !checked);
    return <MemberContactAccessGrant contactId={contactId} checked={checked} disabled={disabled}
      label={applyCmpOrganizationNoun(contactAccessGrantLabel, noun)}
      nudge={nudge} note={sameAsPrimary ? contactSameAsPrimaryAccessMessage : undefined}
      nudgeTitle={sameAsPrimary ? contactSameAsPrimaryAccessMessage : undefined}
      reason={reason} onChange={enabled => setAccessDraft(contactId, enabled)} />;
  };
  const renderCredentials = (contactId: string, hasUnsavedChanges: boolean) => {
    const account = data?.getCmpMemberContactAccounts.find(row => row.contactId === contactId);
    if (!account) return <p role="status" className="text-[13px] text-[#727272]">{loading ? "Loading contact access..." : error ? CONTACT_ACCESS_NOT_LOADED_TEXT : "Member Dashboard access is not ready for this contact. Select Save Changes for this contact, then refresh the page."}</p>;
    const legacy = account.usesLegacyMemberAccount ? legacyCredentials : undefined;
    const draftLinked = isContactAccessDraftLinked(contactId) && !account.usesLegacyMemberAccount;
    const draft = draftLinked ? accessDrafts[contactId] : undefined;
    const displayEnabled = draft ?? account.enabled ?? null;
    const actionsDisabled = !!account.credentialActionsBlockedReason || busy || loading || readOnly || refreshRequired || !!error || !account.email ||
      (!!account.usesLegacyMemberAccount && (!legacy || !!legacy.disabled));
    return <>
      <MemberAdministratorCredentials lifecycleStatus={lifecycleStatus} hasSignedUp={account?.hasSignedUp}
        showPermissionNote={contactId !== "primaryVoting" && !contactId.startsWith("legacyMemberAccount") && displayEnabled === true}
        accessEnabled={displayEnabled} allowCredentialPreparation={!!legacy}
        onAccessChange={account.usesLegacyMemberAccount ? undefined : draftLinked
          ? enabled => setAccessDraft(contactId, enabled)
          : enabled => void execute(contactId, enabled ? Action.Enable : Action.Disable)}
        accessDraftMode={draftLinked} accessLockedTitle={contactAccessLockedTitle}
        accessEnabledAtUtcMilli={draft === true && account.enabled !== true ? draftEnabledAt.current[contactId] : account?.accessEnabledAtUtcMilli}
        dashboardLoginCountLast12Months={account?.dashboardLoginCountLast12Months}
        credentialsSentAtUtcMilli={account.credentialsSentAtUtcMilli}
        lastPasswordResetAtUtcMilli={account?.lastPasswordResetAtUtcMilli}
        lastDashboardLoginAtUtcMilli={account?.lastDashboardLoginAtUtcMilli} chamberTimeZone={timeZone}
        hasUnsavedLoginEmail={hasUnsavedChanges || draft !== undefined || !!legacy?.hasUnsavedLoginEmail}
        disabled={actionsDisabled}
        accessChangeDisabled={busy || loading || readOnly || refreshRequired || !!error || !!account.usesLegacyMemberAccount || (!account.enabled && !!account.credentialActionsBlockedReason)}
        onSend={() => {
          const send = legacy
            ? legacy.onSend
            : () => setPending({ contactId, name: account.name || "", email: account.email, action: Action.SendCredentials });
          if (account.credentialsSentAtUtcMilli && !account.hasSignedUp)
            setResendWarning({ name: account.name || "", email: account.email, sentAtUtcMilli: account.credentialsSentAtUtcMilli, onProceed: send });
          else send();
        }}
        onReset={() => { if (legacy) legacy.onReset(); else setPending({ contactId, name: account.name || "", email: account.email, action: Action.ResetPassword }); }} />
      {account.credentialActionsBlockedReason && <p role="status" className="text-sm text-[#727272]">{account.credentialActionsBlockedReason}</p>}
      {failure?.contactId === contactId && <p role="alert" className="text-sm text-[#E24C4C]">{failure.message}</p>}
    </>;
  };
  const feedback = <>
    <DesignDialog open={!!resendWarning} onClose={() => setResendWarning(null)}
      icon="warningTriangle" iconColor="#E1A731" title="Reset Contact Credentials?"
      message={<>
        <p className="m-0">Credentials were last sent to {resendWarning?.name || resendWarning?.email} on {resendWarning ? DateTime.fromMillis(resendWarning.sentAtUtcMilli, { zone: normalizeCmpTimeZone(timeZone).timeZone }).toFormat("MMM d, yyyy 'at' h:mm a", { locale: "en-US" }) : ""}, but they have not logged in yet.</p>
        <p className="m-0 mt-[10px]">Resetting their password will invalidate the previously sent credentials. They must use the new credentials to sign in.</p>
        <p className="m-0 mt-[10px] font-bold">Do you want to continue?</p>
      </>}
      actions={<DesignDialogButton tone="green" onClick={() => {
        const proceed = resendWarning?.onProceed;
        setResendWarning(null);
        proceed?.();
      }}>PROCEED</DesignDialogButton>} />
    {error && <p role="alert" className="text-sm text-[#E24C4C]">{getUserFacingErrorText(error, `We could not load Member Dashboard access for this member's contacts. Click Retry. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`)} <button className="underline" onClick={() => void refetch()}>Retry</button></p>}
    <SendCredentialsDialog open={!!pending && pending.action !== Action.ResetPassword} mode={previewOpen ? "preview" : "options"}
      loading={busy || (previewOpen && sample.loading)} disabled={readOnly || refreshRequired || !credentialsAllowed}
      error={failure?.message || credentialLifecycleBlock || preview.error} subject={preview.subject} html={preview.html}
      recipientName={pending?.name || ""} recipientEmail={pending?.email || ""}
      onDownloadCsv={() => { if (pending) void execute(pending.contactId, Action.DownloadCredentials, "csv", { name: pending.name, email: pending.email }); }}
      onDownloadEmail={() => { if (pending) void execute(pending.contactId, Action.DownloadCredentials, "html", { name: pending.name, email: pending.email }); }}
      onPreview={() => { if (credentialsAllowed) setPreviewOpen(true); }}
      onSend={() => { if (pending) void execute(pending.contactId, pending.action, "csv", { name: pending.name, email: pending.email }); }}
      onClose={() => { if (!busy) { if (previewOpen) setPreviewOpen(false); else setPending(null); } }} />
    <DesignDialog open={pending?.action === Action.ResetPassword} onClose={() => { if (!busy) setPending(null); }} busy={busy}
      icon="lock" title="Reset Password"
      message={<>Are you sure you want to reset the member dashboard password for <strong>{pending?.name || pending?.email}</strong>? The password is reset for this contact only, and they will show as not logged in until they sign in with their new password.</>}
      actions={<DesignDialogButton tone="dark" disabled={busy || readOnly || refreshRequired || !credentialsAllowed}
        onClick={() => { if (pending) void execute(pending.contactId, Action.ResetPassword, "csv", { name: pending.name, email: pending.email }); }}>Reset</DesignDialogButton>}>
      {!credentialsAllowed && <p role="alert" className="mt-3 text-sm text-[#E24C4C]">{credentialLifecycleBlock}</p>}
    </DesignDialog>
    <ActionSuccessDialog message={success?.message} highlights={success?.highlights} onClose={() => setSuccess(null)}
      note={success?.note || success?.warning ? <>{success.note}{success.warning && <span role="alert" className={`block text-[#92400E] ${success.note ? "mt-[10px]" : ""}`}>{success.warning}</span>}</> : undefined} />
  </>;
  return { renderAccessGrant, renderCredentials, applyAccessDraft, isAccessChecked, feedback, unmatchedLegacyAccounts: data?.getCmpMemberContactAccounts.filter(row => row.contactId.startsWith("legacyMemberAccount")) || [] };
}
